/**
* @prop --card-heading-color: color of the heading. Defaults to `--contrast-1100`;
* @prop --card-subheading-color: color of the sub heading. Defaults to `--contrast-1000`;
* @prop --card-border-radius: border radius of the card. Defaults to `0.95rem`;
* @prop --card-background-color: background color of the card.
* @prop --card-background-color--hovered: background color of the card, when hovered.
*/

@use '../../style/mixins';

$default-border-radius: 0.95rem;

* {
    box-sizing: border-box;
    min-width: 0;
    min-height: 0;
}

:host(limel-card) {
    display: flex;
    border-radius: var(--card-border-radius, $default-border-radius);
}

section {
    box-sizing: border-box;

    display: flex;
    gap: 0.5rem;

    flex-direction: column;
    :host(limel-card[orientation='landscape']) & {
        flex-direction: row;
    }

    width: 100%;
    border-radius: var(--card-border-radius, $default-border-radius);
    border: 1px solid rgb(var(--contrast-500));

    padding: 0.25rem;
    background-color: var(--card-background-color, rgb(var(--contrast-300)));

    &:hover {
        border-color: transparent;
        background-color: var(
            --card-background-color--hovered,
            var(--card-background-color, rgb(var(--contrast-200)))
        );
    }
}

.body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

img {
    transition: filter 0.6s ease;
    object-fit: cover;
    border-radius: calc(
        var(--card-border-radius, $default-border-radius) / 1.4
    );
    :host(limel-card[orientation='portrait']) & {
        width: 100%;
    }

    :host(limel-card[orientation='landscape']) & {
        flex-shrink: 0;

        max-width: 40%;
        height: 100%;
    }

    section:hover &,
    section:focus-visible & {
        transition-duration: 0.2s;
        filter: saturate(1.3);
    }
}

limel-markdown {
    overflow-y: auto;
    padding: 0.5rem 0.75rem;
}

header {
    flex-shrink: 0;
    display: flex;
    justify-content: center;

    gap: 0.5rem;

    padding: 0.25rem 0.75rem;
    :host(limel-card[orientation='landscape']) & {
        padding-top: 0.5rem;
    }

    &:has(limel-icon) {
        padding-left: 0.25rem;
    }

    .headings {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
    }

    .title {
        padding-right: 1.25rem;
    }

    limel-icon {
        flex-shrink: 0;
        width: 2rem;
    }

    h1 {
        font-size: 1.125rem;
        font-weight: 500;
        color: var(--card-heading-color, rgb(var(--contrast-1100)));
        letter-spacing: -0.03125rem; // 0.5px
    }

    h2 {
        font-size: var(--limel-theme-default-font-size);
        font-weight: 400;
        color: var(--card-subheading-color, rgb(var(--contrast-1000)));
    }

    h1,
    h2 {
        word-break: break-word;
        hyphens: auto;
        -webkit-hyphens: auto;
        margin: 0;
    }
}

limel-action-bar {
    flex-shrink: 0;
    --action-bar-background-color: transparent;
    margin-left: auto;
}

// The 3D effect
limel-3d-hover-effect-glow {
    border-radius: var(--card-border-radius, $default-border-radius);
}

:host(limel-card) {
    @include mixins.parent-of-the-3d-element;
}

section {
    @include mixins.the-3d-element;

    :host(limel-card[clickable]) & {
        @include mixins.the-3d-element--clickable;
    }
}
